import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CardMembershipRounded,
  LocationOnRounded,
  CalendarTodayRounded,
  AutorenewRounded,
  CancelRounded,
  CloseRounded,
  ScheduleRounded,
  CheckCircleRounded,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { differenceInDays, format, parseISO, isValid } from 'date-fns';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import PageShell from '../../components/shared/PageShell';
import SectionHeader from '../../components/shared/SectionHeader';
import SurfaceCard from '../../components/shared/SurfaceCard';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import { RADIUS } from '../../theme/tokens';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { listContainerVariants, listItemVariants, duration, easing } from '../../theme/motion';

const MotionBox = motion(Box);

// --- Local sub-component: SubscriptionCard ---

function ExpiryChip({ expiresAt }) {
  const date = typeof expiresAt === 'string' ? parseISO(expiresAt) : expiresAt;
  if (!date || !isValid(date)) return null;

  const days = differenceInDays(date, new Date());
  if (days >= 3) return null;

  let label;
  if (days <= 0) label = 'Expires today';
  else if (days === 1) label = 'Expires tomorrow';
  else label = `Expires in ${days} days`;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        fontWeight: 700,
        fontSize: '0.7rem',
      }}
    />
  );
}

function SubscriptionCard({ subscription, variant, onToggleAutoRenew, onCancel }) {
  const isActive = variant === 'active';

  const startDate = subscription.start_date
    ? format(parseISO(subscription.start_date), 'd MMM yyyy')
    : '—';
  const endDate = subscription.end_date
    ? format(parseISO(subscription.end_date), 'd MMM yyyy')
    : '—';

  if (isActive) {
    return (
      <SurfaceCard
        radius="lg"
        elevation="sm"
        padding={0}
        sx={{
          mb: 2,
          overflow: 'hidden',
          borderLeft: '4px solid',
          borderLeftColor: 'primary.main',
        }}
      >
        {/* Header band */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
                {subscription.library?.name ?? '—'}
              </Typography>
              {subscription.library?.city && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <LocationOnRounded sx={{ fontSize: 13, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {subscription.library.city}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Chip
                label={subscription.status?.toUpperCase() ?? 'ACTIVE'}
                size="small"
                icon={<CheckCircleRounded sx={{ fontSize: 13 }} />}
                color="primary"
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
              <ExpiryChip expiresAt={subscription.end_date} />
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Box
          sx={{
            mx: 2,
            mb: 2,
            p: 2,
            bgcolor: 'background.subtle',
            borderRadius: RADIUS.md,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
            {subscription.plan?.name ?? '—'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              {subscription.days_remaining != null ? `${subscription.days_remaining} days remaining` : '—'}
            </Typography>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {subscription.amount_paid_display ?? '—'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarTodayRounded sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {startDate} — {endDate}
            </Typography>
          </Box>
        </Box>

        {/* Auto-renew & cancel row */}
        <Box
          sx={{
            px: 2.5,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(subscription.auto_renew)}
                onChange={() => onToggleAutoRenew(subscription)}
                size="small"
                inputProps={{ 'aria-label': 'Toggle auto-renew' }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AutorenewRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Auto-renew
                </Typography>
              </Box>
            }
          />
          {subscription.status === 'active' && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onCancel(subscription)}
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            >
              Cancel
            </Button>
          )}
        </Box>
      </SurfaceCard>
    );
  }

  // Past variant
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: RADIUS.md,
        p: 2,
        mb: 1.5,
        opacity: 0.72,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          {subscription.library?.name ?? '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subscription.plan?.name ?? '—'}{subscription.amount_paid_display ? ` · ${subscription.amount_paid_display}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <CalendarTodayRounded sx={{ fontSize: 11, color: 'text.tertiary' }} />
          <Typography variant="caption" color="text.tertiary">
            {endDate}
          </Typography>
        </Box>
      </Box>
      <Chip
        label={subscription.status?.toUpperCase() ?? '—'}
        size="small"
        icon={<ScheduleRounded sx={{ fontSize: 12 }} />}
        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
      />
    </Box>
  );
}

// --- Page component ---

function StudentSubscriptions() {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const reducedMotion = useReducedMotionPreferred();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchSubscriptions();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const res = await api.get('/subscriptions');
      setSubscriptions(res.data.data.subscriptions || []);
    } catch (err) {
      setFetchError('Failed to load subscriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggleAutoRenew = useCallback(async (subscription) => {
    const next = !subscription.auto_renew;
    // Optimistic update
    setSubscriptions(prev =>
      prev.map(s => s.id === subscription.id ? { ...s, auto_renew: next } : s)
    );
    try {
      await api.put(`/subscriptions/${subscription.id}/auto-renew`, { auto_renew: next });
      setToast({
        open: true,
        message: `Auto-renew ${next ? 'enabled' : 'disabled'}`,
        severity: 'success',
      });
    } catch (err) {
      // Revert on error
      setSubscriptions(prev =>
        prev.map(s => s.id === subscription.id ? { ...s, auto_renew: !next } : s)
      );
      setToast({ open: true, message: 'Failed to update auto-renew', severity: 'error' });
    }
  }, []);

  const handleCancelSubscription = useCallback(async () => {
    if (!cancelTarget) return;
    try {
      setCancelLoading(true);
      await api.post(`/subscriptions/${cancelTarget.id}/cancel`);
      setToast({ open: true, message: 'Subscription cancelled', severity: 'success' });
      setCancelTarget(null);
      fetchSubscriptions();
    } catch (err) {
      setToast({
        open: true,
        message: err.response?.data?.message || 'Failed to cancel subscription',
        severity: 'error',
      });
    } finally {
      setCancelLoading(false);
    }
  }, [cancelTarget, fetchSubscriptions]);

  const activeSubscriptions = subscriptions.filter(s => s.is_active);
  const pastSubscriptions = subscriptions.filter(s => !s.is_active);

  const containerMotion = reducedMotion
    ? {}
    : { variants: listContainerVariants, initial: 'initial', animate: 'animate' };

  const itemMotion = reducedMotion
    ? {}
    : { variants: listItemVariants };

  if (loading) {
    return (
      <PageShell title="Subscriptions" backTo={-1}>
        <LoadingSkeleton variant="hero" />
        <LoadingSkeleton variant="list" count={3} />
      </PageShell>
    );
  }

  if (fetchError) {
    return (
      <PageShell title="Subscriptions" backTo={-1}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchSubscriptions}>
              Retry
            </Button>
          }
        >
          {fetchError}
        </Alert>
      </PageShell>
    );
  }

  const hasNoSubscriptions = subscriptions.length === 0;

  return (
    <PageShell title="Subscriptions" backTo={-1}>
      {hasNoSubscriptions ? (
          <EmptyState
            icon={<CardMembershipRounded />}
            title="No active subscriptions"
            description="Subscribe to a library plan for unlimited access to your favourite study space."
            actionLabel="Browse plans"
            onAction={() => navigate('/student/libraries')}
          />
        ) : (
          <MotionBox {...containerMotion}>
            {/* Active subscription */}
            {activeSubscriptions.length > 0 && (
              <MotionBox {...itemMotion} sx={{ mb: 3 }}>
                {activeSubscriptions.map(sub => (
                  <SubscriptionCard
                    key={sub.id}
                    subscription={sub}
                    variant="active"
                    onToggleAutoRenew={handleToggleAutoRenew}
                    onCancel={setCancelTarget}
                  />
                ))}
              </MotionBox>
            )}

            {/* Past subscriptions — omit section entirely if none */}
            {pastSubscriptions.length > 0 && (
              <MotionBox {...itemMotion}>
                <SectionHeader
                  title="Past subscriptions"
                  icon={<ScheduleRounded sx={{ fontSize: 18 }} />}
                />
                <AnimatePresence>
                  {pastSubscriptions.map(sub => (
                    <MotionBox
                      key={sub.id}
                      {...itemMotion}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <SubscriptionCard
                        subscription={sub}
                        variant="past"
                        onToggleAutoRenew={handleToggleAutoRenew}
                        onCancel={setCancelTarget}
                      />
                    </MotionBox>
                  ))}
                </AnimatePresence>
              </MotionBox>
            )}
          </MotionBox>
        )}

      {/* Cancel confirmation dialog */}
      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>Cancel subscription</Typography>
            <IconButton onClick={() => setCancelTarget(null)} aria-label="Close dialog" size="small">
              <CloseRounded />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel this subscription?
          </Alert>
          {cancelTarget && (
            <Box>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {cancelTarget.library?.name} — {cancelTarget.plan?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can use it until{' '}
                {cancelTarget.end_date
                  ? format(parseISO(cancelTarget.end_date), 'd MMM yyyy')
                  : '—'}
                . Auto-renewal will be disabled.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setCancelTarget(null)}>
            Keep subscription
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelSubscription}
            disabled={cancelLoading}
            startIcon={
              cancelLoading
                ? <CircularProgress size={16} color="inherit" />
                : <CancelRounded />
            }
          >
            Cancel subscription
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <AnimatePresence>
        {toast.open && (
          <MotionBox
            key="toast"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: duration.fast, ease: easing.standard }}
            sx={{
              position: 'fixed',
              bottom: 96,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2000,
              minWidth: 260,
            }}
          >
            <Alert
              severity={toast.severity}
              onClose={() => setToast(t => ({ ...t, open: false }))}
              sx={{ borderRadius: RADIUS.md, boxShadow: 6 }}
            >
              {toast.message}
            </Alert>
          </MotionBox>
        )}
      </AnimatePresence>
    </PageShell>
  );
}

export default StudentSubscriptions;
